<script setup lang="ts">
  import { ref } from 'vue';

  import type { EmailSender } from '@stuut/api/models';
  import { AlertDialog } from '@ui/components/alert-dialog';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import EmailProviderLogo from '@/components/email-senders/EmailProviderLogo.vue';
  import EmailSenderDropdown from '@/components/email-senders/EmailSenderDropdown.vue';
  import EmailSignatureEditorDialog from '@/components/email-senders/EmailSignatureEditorDialog.vue';
  import { useProfile } from '@/store';

  interface Props {
    emailSender?: EmailSender;
    isPrimary?: boolean;
  }

  defineProps<Props>();

  const emits = defineEmits<{
    (e: 'connect'): void;
    (e: 'reconnect'): void;
    (e: 'deleted'): void;
  }>();

  const profileStore = useProfile();

  const signatureEditorOpen = ref(false);
</script>

<template>
  <div class="grid grid-cols-[min-content_1fr_150px] gap-x-3">
    <div class="flex flex-row justify-center items-center">
      <EmailProviderLogo :provider="emailSender?.provider" />
    </div>

    <div class="flex flex-row gap-3 items-center">
      <div class="flex flex-col justify-center">
        <span class="text-sm font-medium block">
          {{ emailSender?.provider ? emailSender.email : 'Link Email' }}
        </span>
        <span v-if="!emailSender" class="text-xs text-muted-foreground">
          Link your email to enable seamless outreach.
        </span>
      </div>

      <div v-if="emailSender" class="flex flex-row items-center gap-2">
        <Button
          v-if="emailSender.is_nylas_grant_expired"
          variant="ghost"
          class="flex flex-row items-center text-primary"
          @click="emits('reconnect')"
        >
          Reconnect <i class="fa fa-angle-right text-xs" />
        </Button>

        <Badge
          v-if="profileStore.profile.default_email_sender_id === emailSender.id"
          variant="agent"
          size="sm"
        >
          Primary
        </Badge>

        <Badge v-if="emailSender.is_shared" variant="agent" size="sm"> Shared </Badge>

        <Badge :variant="emailSender.signature_html ? 'agent' : 'warning'" size="sm">
          {{ emailSender.signature_html ? 'Signature' : 'No Signature' }}
        </Badge>

        <Tooltip v-if="emailSender.is_hybrid_sender">
          <TooltipTrigger>
            <Badge variant="secondary" class="my-auto" size="sm"> Hybrid </Badge>
          </TooltipTrigger>
          <TooltipContent> Read via {{ emailSender.provider }}, Send via SendGrid </TooltipContent>
        </Tooltip>
      </div>
    </div>

    <div class="flex flex-row justify-end items-center gap-2">
      <EmailSenderDropdown
        v-if="emailSender"
        :email-sender="emailSender"
        @deleted="emits('deleted')"
        @edit-signature="signatureEditorOpen = true"
      />

      <Button
        v-else
        variant="ghost"
        class="flex flex-row items-center text-primary"
        size="sm"
        @click="emits('connect')"
      >
        Connect <i class="fa fa-angle-right text-xs" />
      </Button>
    </div>
  </div>

  <AlertDialog v-if="emailSender" v-model:open="signatureEditorOpen">
    <EmailSignatureEditorDialog
      v-if="signatureEditorOpen"
      :email-sender="emailSender"
      @close="signatureEditorOpen = false"
    />
  </AlertDialog>
</template>
